import React, { useEffect, useState } from "react";
import { Plus, Search, Trash2, Mail, Phone, Building } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const empty = { name: "", email: "", phone: "", company: "", role: "buyer", notes: "", investor_budget_min: 0, investor_budget_max: 0, investor_cities: "", investor_types: [], investor_roi_min: 0, investor_liquidity: 0 };

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/contacts", { params: q ? { q } : {} }); setItems(data); };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        investor_budget_min: Number(form.investor_budget_min) || 0,
        investor_budget_max: Number(form.investor_budget_max) || 0,
        investor_roi_min: Number(form.investor_roi_min) || 0,
        investor_liquidity: Number(form.investor_liquidity) || 0,
        investor_cities: (form.investor_cities || "").split(",").map((s) => s.trim()).filter(Boolean),
        investor_types: form.investor_types || [],
      };
      await api.post("/contacts", payload); toast.success("Contatto creato"); setOpen(false); setForm(empty); load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/contacts/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const roleColor = { buyer: "bg-blue-100 text-blue-700", investor: "bg-emerald-100 text-emerald-700", lawyer: "bg-amber-100 text-amber-700", notary: "bg-violet-100 text-violet-700" };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Contatti</h1>
          <p className="text-muted-foreground text-sm mt-1">Rubrica di acquirenti, investitori e collaboratori.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-contact-button"><Plus className="w-4 h-4" /> Nuovo contatto</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo contatto</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Azienda</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                <div><Label>Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">Acquirente</SelectItem>
                      <SelectItem value="investor">Investitore</SelectItem>
                      <SelectItem value="lawyer">Avvocato</SelectItem>
                      <SelectItem value="notary">Notaio</SelectItem>
                      <SelectItem value="other">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Note</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              {(form.role === "investor" || form.role === "buyer") && (
                <div className="pt-3 border-t border-border/60 space-y-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preferenze investitore</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Budget min (€)</Label><Input type="number" value={form.investor_budget_min} onChange={(e) => setForm({ ...form, investor_budget_min: e.target.value })} data-testid="contact-budget-min" /></div>
                    <div><Label className="text-xs">Budget max (€)</Label><Input type="number" value={form.investor_budget_max} onChange={(e) => setForm({ ...form, investor_budget_max: e.target.value })} data-testid="contact-budget-max" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">ROI minimo (%)</Label><Input type="number" step="0.1" value={form.investor_roi_min} onChange={(e) => setForm({ ...form, investor_roi_min: e.target.value })} data-testid="contact-roi-min" /></div>
                    <div><Label className="text-xs">Liquidità disponibile (€)</Label><Input type="number" value={form.investor_liquidity} onChange={(e) => setForm({ ...form, investor_liquidity: e.target.value })} data-testid="contact-liquidity" /></div>
                  </div>
                  <div><Label className="text-xs">Città preferite (separate da virgola)</Label><Input value={form.investor_cities} onChange={(e) => setForm({ ...form, investor_cities: e.target.value })} placeholder="Milano, Roma, Firenze" data-testid="contact-cities" /></div>
                  <div><Label className="text-xs">Tipologie</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {["residential", "commercial", "land", "industrial"].map((t) => {
                        const active = form.investor_types.includes(t);
                        return (
                          <button key={t} type="button"
                            onClick={() => setForm({ ...form, investor_types: active ? form.investor_types.filter(x => x !== t) : [...form.investor_types, t] })}
                            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-muted"}`}
                            data-testid={`type-toggle-${t}`}
                          >{t}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={submit} disabled={!form.name} data-testid="contact-submit-button">Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <Card className="border-border/60 p-4">
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca contatti…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contacts-search" /></div>
      </Card>

      <Card className="border-border/60 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nome</TableHead><TableHead>Ruolo</TableHead><TableHead>Azienda</TableHead><TableHead>Email</TableHead><TableHead>Telefono</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-16">Nessun contatto ancora.</TableCell></TableRow>}
            {items.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell><Badge className={`${roleColor[c.role] || "bg-muted text-muted-foreground"} border-transparent`}>{c.role}</Badge></TableCell>
                <TableCell className="text-sm">{c.company && <span className="inline-flex items-center gap-1"><Building className="w-3 h-3" />{c.company}</span>}</TableCell>
                <TableCell className="text-sm">{c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}</TableCell>
                <TableCell className="text-sm">{c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`del-contact-${c.id}`}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
